// Fetch wrapper. Same-origin only (the BFF is the sole backend the client
// knows about — the Odoo URL is never exposed). Sends the custom header the
// BFF's CSRF gate requires, and normalises errors into one shape the UI can
// branch on.

export type ApiErrorKind =
  | 'needSession'
  | 'needOperator'
  | 'rateLimited'
  | 'validation'
  | 'business'
  | 'offline'
  | 'server';

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly code: string;
  readonly status: number;
  readonly retryAfterSec: number | undefined;

  constructor(kind: ApiErrorKind, code: string, message: string, status: number, retryAfterSec?: number) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.code = code;
    this.status = status;
    this.retryAfterSec = retryAfterSec;
  }
}

function kindFor(code: string, status: number, body: Record<string, unknown>): ApiErrorKind {
  if (body['needOperator']) return 'needOperator';
  if (body['needSession'] || code === 'no_session' || code === 'session_expired') return 'needSession';
  if (status === 429) return 'rateLimited';
  if (code === 'odoo_user_error') return 'business';
  if (status === 400 || status === 415 || status === 413) return 'validation';
  return 'server';
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      credentials: 'same-origin',
      ...init,
      headers: {
        Accept: 'application/json',
        'X-SF-Api': '1',
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
    });
  } catch (cause) {
    throw new ApiError('offline', 'network', 'No connection to the server.', 0);
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    throw new ApiError('server', 'bad_response', 'The server sent an unexpected response.', res.status);
  }

  if (!res.ok) {
    const err = (body as { error?: Record<string, unknown> }).error ?? {};
    const code = String(err['code'] ?? 'error');
    const message = String(err['message'] ?? 'Something went wrong.');
    const retry = typeof err['retryAfterSec'] === 'number' ? err['retryAfterSec'] : undefined;
    throw new ApiError(kindFor(code, res.status, err), code, message, res.status, retry);
  }
  return body as T;
}

export const http = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) }),
};

/** Allow-listed call_kw through the BFF. */
export async function callKw<T>(
  model: string,
  method: string,
  args: unknown[] = [],
  kwargs: Record<string, unknown> = {},
): Promise<T> {
  const { result } = await http.post<{ result: T }>('/api/call', { model, method, args, kwargs });
  return result;
}

/** search_read helper — the BFF validates fields/domain/order against its whitelist. */
export function searchRead<T>(
  model: string,
  domain: unknown[],
  fields: string[],
  opts: { limit?: number; offset?: number; order?: string } = {},
): Promise<T[]> {
  return callKw<T[]>(model, 'search_read', [], { domain, fields, ...opts });
}
